import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { AccountLogic, BrowserSupportStore } from '@mwe/services';
import { TranslateService } from '@ngx-translate/core';
import { finalize, take, tap } from 'rxjs/operators';
import { NotificationBannerComponent } from '../../notification-banner/notification-banner.component';
import { GlobalNotificationStore } from './global-notifications.store';
import { from, isObservable, Observable, of } from 'rxjs';
import { Notification } from '@mwe/models';

@Component({
  selector: 'mwe-global-notification-wrapper',
  imports: [NotificationBannerComponent],
  templateUrl: './global-notification-wrapper-component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalNotificationWrapperComponent implements OnInit {
  private readonly browserSupportStore = inject(BrowserSupportStore);
  private readonly accountLogic = inject(AccountLogic);
  private readonly translateService = inject(TranslateService);
  private readonly notificationsStore = inject(GlobalNotificationStore);

  // Banner Array
  readonly globalNotifications = computed(() => this.notificationsStore.notifications());

  // Browser Incompatibility
  private readonly showIncompatibilityBanner = computed(() => this.browserSupportStore.showIncompatibilityBanner());

  ngOnInit() {
    // Init Impersonate Banner
    if (this.accountLogic.isLoggedIn() && this.accountLogic.isImpersonate()) {
      this.translateService
        .get('global.impersonate.warningText', { email: this.accountLogic.getAccountEmail() })
        .pipe(
          tap(translation => {
            const impersonateNotification = this.createImpersonateBanner(translation);
            this.notificationsStore.addNotification(impersonateNotification);
          }),
        )
        .subscribe();
    }

    // Init Browser Incompatibility Banner
    if (this.showIncompatibilityBanner()) {
      this.notificationsStore.addNotification(this.createIncompatibilityBanner());
    }
  }

  onCloseClick(notification: Notification) {
    if (!notification.isClosable) {
      this.notificationsStore.removeNotification(notification);
      return;
    }

    const result = notification.closeCallback(notification);
    this.toObservable(result)
      .pipe(
        take(1),
        finalize(() => this.notificationsStore.removeNotification(notification)),
      )
      .subscribe();
  }

  private toObservable(result: void | Promise<void> | Observable<unknown>) {
    if (isObservable(result)) {
      return result;
    }

    if (result instanceof Promise) {
      return from(result);
    }

    return of(result);
  }

  private createImpersonateBanner(text: string) {
    return {
      type: 'danger',
      text,
      emphasis: 'bold',
    } satisfies Notification;
  }

  private createIncompatibilityBanner() {
    return {
      emphasis: 'bold',
      text: 'global.browserSupportMessage',
      type: 'warning',
      isClosable: true,
      closeCallback: () => {
        this.browserSupportStore.updateUserSelection(true);
      },
    } satisfies Notification;
  }
}
